import React, { useMemo } from 'react';
import get from 'lodash/get';

import CollapsePanel from '~/components/CollapsePanel';
import { SimpleInfo } from '~/components';
import ActionManager, { ActionType } from '~/components/ActionManager';
import { Flex } from '~/ui/components';

import ProductTags from './components/ProductTags';
import defaultFields from './fields/defaultFields';
import { requestCancelFields } from './fields/extraFields';
import { Block, Column, Wrapper } from './ContractFieldsPanel.styled';

export type ContractFieldsData = {
  [key: string]: any;
  infos: any;
  showAgreementPassword: boolean;
  canEditBornDate: boolean;
  user: any;
  contractIdParam: string;
  conditionChangedFields?: string[];
};

type ContractFieldsPanelProps = {
  data: ContractFieldsData;
  sections?: string[];
  isConferenceByPersonEnabled?: boolean;
  compact?: boolean;
};

export default function ContractFieldsPanel({
  data,
  sections,
  isConferenceByPersonEnabled,
  compact,
}: ContractFieldsPanelProps) {
  const activeFields = useMemo(() => {
    const { isRefusalRequested, openRefusalReason, portOutStepLogs, turingContractDetailsResponseDTO } = data;
    const hasOutstandingBalance = !!(
      turingContractDetailsResponseDTO?.outstandingBalance?.date ||
      turingContractDetailsResponseDTO?.outstandingBalance?.value
    );
    const hasEmployerInfo = !!(
      turingContractDetailsResponseDTO?.employer?.cnpj || turingContractDetailsResponseDTO?.employer?.cnae
    );

    let available = [...defaultFields];

    if (!portOutStepLogs) {
      available = available.filter((f) => f.type !== 'PORT_OUT_LOGS');
    }
    if (!hasOutstandingBalance) {
      available = available.filter((f) => f.type !== 'OUTSTANDING_BALANCE');
    }
    if (!hasEmployerInfo) {
      available = available.filter((f) => f.type !== 'EMPLOYER_INFO');
    }
    if (isRefusalRequested || openRefusalReason) {
      available = [...available, ...requestCancelFields];
    }
    if (sections?.length) {
      available = available.filter((f) => sections.includes(f.title));
    }

    return available;
  }, [data, sections]);

  const defaultActiveKey = useMemo(() => activeFields.map((f) => f.title), [activeFields]);

  return (
    <Wrapper compact={compact}>
      <CollapsePanel defaultActiveKey={defaultActiveKey}>
        {activeFields.map((block) => (
          <CollapsePanel.Panel header={block.title} key={block.title}>
            <Block>
              {block.columns.map((items, columnIndex) => (
                // eslint-disable-next-line react/no-array-index-key
                <Column key={columnIndex}>
                  {items?.map(
                    (
                      {
                        actionType,
                        value,
                        valueColor,
                        modalParams,
                        clickable,
                        tooltip,
                        renderValue,
                        versionKey,
                        ...itemProps
                      },
                      itemIndex,
                    ) => {
                      const actionValue = typeof value === 'string' ? get(data, value, '-') : value(data);
                      const color = typeof valueColor === 'function' ? valueColor(actionValue, data) : valueColor;
                      const resolvedRenderValue =
                        typeof renderValue === 'function' ? renderValue(data, actionValue) : renderValue;
                      const type = typeof actionType === 'function' ? actionType(data) : (actionType as ActionType);
                      const isClickable = typeof clickable === 'function' ? !!clickable(data) : clickable;
                      const tooltipValue = typeof tooltip === 'function' ? tooltip(data) : tooltip;

                      if (type === 'product_tags') {
                        return (
                          <SimpleInfo
                            // eslint-disable-next-line react/no-array-index-key
                            key={itemIndex}
                            label={itemProps.label}
                            renderValue={<ProductTags data={actionValue} />}
                          />
                        );
                      }

                      if (type === 'group_contracts') {
                        if (!actionValue?.length || actionValue === '-') {
                          // eslint-disable-next-line react/no-array-index-key
                          return <SimpleInfo key={itemIndex} label={itemProps.label} renderValue="-" />;
                        }
                        return (
                          <SimpleInfo
                            // eslint-disable-next-line react/no-array-index-key
                            key={itemIndex}
                            label={itemProps.label}
                            renderValue={
                              <Flex wrap="wrap" gap={4}>
                                {actionValue.map((groupContract: any) => (
                                  <span key={groupContract.contractId}>#{groupContract.contractId}</span>
                                ))}
                              </Flex>
                            }
                          />
                        );
                      }

                      return (
                        <ActionManager
                          // eslint-disable-next-line react/no-array-index-key
                          key={itemIndex}
                          {...{
                            ...itemProps,
                            valueColor: color,
                            renderValue: resolvedRenderValue,
                            modalParams: modalParams?.(data),
                            clickable: isClickable,
                            tooltip: tooltipValue,
                            disabled: isConferenceByPersonEnabled && data.isGroupContract && !data.isGroupLead,
                            changed: !!(versionKey && data.conditionChangedFields?.includes(versionKey)),
                          }}
                          value={actionValue}
                          type={type}
                          child={SimpleInfo}
                        />
                      );
                    },
                  )}
                </Column>
              ))}
            </Block>
          </CollapsePanel.Panel>
        ))}
      </CollapsePanel>
    </Wrapper>
  );
}
